import {useMutation, useQueryClient, useQuery, keepPreviousData, useInfiniteQuery} from '@github-ui/react-query'
import type {InfiniteData} from '@github-ui/react-query'
import {
  COPILOT_SPACES_QUERY_KEY,
  COPILOT_SPACES_COUNT_QUERY_KEY,
  copilotSpacesCategoryQueryKey,
  copilotSpacesQueryKey,
  deleteCopilotSpace,
  fetchCopilotSpace,
  fetchCopilotSpaces,
  fetchMenuActions,
  fetchPaginatedCopilotSpaces,
  fetchSpacesCount,
  fetchVisibilitySettings,
  searchCopilotSpaces,
  DEFAULT_PER_PAGE,
} from '@github-ui/copilot-shared-ui/spaces/fetch'
import type {PaginatedCopilotSpacesResult} from '@github-ui/copilot-shared-ui/spaces/fetch'
import type {
  CopilotSpaceCategory,
  CopilotSpaceId,
  CopilotSpace,
  CopilotSpacePayload,
  IndexCopilotSpace,
} from '@github-ui/copilot-chat-types'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useMemo, useCallback, useSyncExternalStore} from 'react'
import {useChatStateValue} from '@github-ui/copilot-chat/CopilotChatContext'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {getCopilotSpacePath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'

export class CopilotSpaceNotFoundError extends Error {
  constructor(message = 'Copilot Space not found') {
    super(message)
    this.name = 'CopilotSpaceNotFoundError'
  }
}

export class CopilotSpaceSSOError extends Error {
  protectedOrganizations: string[]
  constructor(protectedOrganizations: string[], message = 'Copilot Space SSO error') {
    super(message)
    this.protectedOrganizations = protectedOrganizations
    this.name = 'CopilotSpaceSSOError'
  }
}

export function useDeleteCopilotSpace() {
  const queryClient = useQueryClient()
  const manager = useChatManager()
  return useMutation({
    mutationFn: async (item: CopilotSpaceId) => {
      const result = await deleteCopilotSpace(item)
      // Ensure result is not an error type before returning
      if (!result.ok) {
        throw new Error(result.error)
      }

      // remove the deleted space from the chat manager state
      manager.dispatch({type: 'REMOVE_CUSTOM_COPILOT', customCopilotId: item})
      // Only return the payload or a safe object
      return {status: result.status, ok: true, payload: result.payload}
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: COPILOT_SPACES_QUERY_KEY})
    },
  })
}

/**
 * Hook for fetching custom copilots by category from the server.
 * @param category - The category of spaces to fetch (starred, user_owned, org_owned, shared_with_user)
 * @param enabled - Whether to enable the fetching
 * @param options - Additional query options
 */
export function useFetchCopilotSpacesByCategory(
  category: CopilotSpaceCategory,
  enabled: boolean,
  options: {throwOnError?: boolean} = {},
) {
  return useQuery({
    queryKey: copilotSpacesCategoryQueryKey(category),
    queryFn: () => fetchCopilotSpaces(category),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    throwOnError: options.throwOnError,
  })
}

/**
 * Hook for fetching filtered custom copilots by category from the server.
 * @param category - The category of spaces to fetch (starred, user_owned, org_owned, shared_with_user)
 * @param query - The search query string to filter spaces by
 * @param enabled - Whether to enable the fetching
 * @param options - Additional query options
 */
export function useFetchFilteredCopilotSpacesByCategory(
  category: CopilotSpaceCategory,
  query: string,
  enabled: boolean,
  options: {throwOnError?: boolean} = {},
) {
  return useQuery({
    queryKey: [...copilotSpacesCategoryQueryKey(category), 'searchTerm', query],
    queryFn: () => searchCopilotSpaces(category, query),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    throwOnError: options.throwOnError,
    // Keep previous search results visible while new query loads to prevent flickering
    placeholderData: keepPreviousData,
  })
}

/**
 * Hook for fetching paginated custom copilots by category using cursor-based pagination.
 * Uses useInfiniteQuery to support loading more pages via Link header cursors.
 *
 * @param category - The category of spaces to fetch (starred, user_owned, org_owned, shared_with_user)
 * @param enabled - Whether to enable the fetching
 * @param options - Additional query options
 * @param options.perPage - Number of items per page (default: 30, max: 100)
 * @param options.throwOnError - Whether to throw errors to the nearest error boundary
 * @returns Infinite query result with pages of spaces and pagination controls
 */
export function useFetchPaginatedCopilotSpacesByCategory(
  category: CopilotSpaceCategory,
  enabled: boolean,
  options: {perPage?: number; throwOnError?: boolean} = {},
) {
  const {perPage = DEFAULT_PER_PAGE, throwOnError} = options

  return useInfiniteQuery<
    PaginatedCopilotSpacesResult,
    Error,
    InfiniteData<PaginatedCopilotSpacesResult>,
    unknown[],
    string | null
  >({
    queryKey: [...copilotSpacesCategoryQueryKey(category), 'paginated', perPage],
    queryFn: async ({pageParam}) => {
      return fetchPaginatedCopilotSpaces({
        type: category,
        perPage,
        cursor: pageParam,
      })
    },
    initialPageParam: null,
    getNextPageParam: lastPage => lastPage.pagination.nextCursor,
    getPreviousPageParam: firstPage => firstPage.pagination.prevCursor,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    throwOnError,
  })
}

/**
 * Helper hook that flattens paginated spaces data into a single array.
 * Useful for components that need all loaded spaces in a flat list.
 *
 * @param paginatedData - InfiniteData from useFetchPaginatedCopilotSpacesByCategory
 * @returns Flattened array of all loaded spaces
 */
export function useFlattenedPaginatedSpaces(
  paginatedData: InfiniteData<PaginatedCopilotSpacesResult> | undefined,
): IndexCopilotSpace[] {
  return useMemo(() => {
    if (!paginatedData) return []
    return paginatedData.pages.flatMap(page => page.spaces)
  }, [paginatedData])
}

/**
 * The default hook for fetching all custom copilots from the server (without category filter).
 * @deprecated This function fetches all spaces in a single call, which doesn't support independent tab loading.
 * Use useFetchCopilotSpacesByCategory for fetching specific categories, or useManagedFetchCopilotSpaces
 * for automatic fetching of all categories with independent loading. This function may be removed in a future release.
 */
export function useFetchCopilotSpaces(enabled: boolean, options: {throwOnError?: boolean} = {}) {
  return useQuery({
    queryKey: COPILOT_SPACES_QUERY_KEY,
    queryFn: () => fetchCopilotSpaces(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    throwOnError: options.throwOnError,
  })
}

function hasCopilotSpaceData(payload: CopilotSpacePayload): payload is CopilotSpace {
  return (payload as CopilotSpace).id !== undefined
}

export function useFetchCopilotSpace(customCopilotId: CopilotSpaceId | null) {
  return useQuery({
    queryKey: copilotSpacesQueryKey(customCopilotId),
    queryFn: async () => {
      const response = await fetchCopilotSpace(customCopilotId)
      if (!response.ok) {
        if (response.status === 404) {
          throw new CopilotSpaceNotFoundError()
        }
        throw new Error(response.error)
      }
      if (!hasCopilotSpaceData(response.payload)) {
        throw new CopilotSpaceSSOError(response.payload.protectedOrganizations)
      }
      return response.payload
    },
    enabled: !!customCopilotId,
  })
}

// We split up the customCopilotId because the linter wants each parameter used in the query key,
// but directly passing the whole object in seems to key on object identity and lead to lots of
// redundant API calls.
export function useFetchVisibilitySettings(copilotSpaceOwner: string, copilotSpaceNumber: number) {
  return useQuery({
    queryKey: ['visibility-settings', copilotSpaceOwner, copilotSpaceNumber],
    queryFn: async () => {
      return fetchVisibilitySettings({owner: copilotSpaceOwner, id: copilotSpaceNumber})
    },
    enabled: copilotSpaceOwner !== '' && copilotSpaceNumber !== -1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useFetchMenuActions(copilotSpaceOwner: string, copilotSpaceNumber: number) {
  return useQuery({
    queryKey: ['menu-actions', copilotSpaceOwner, copilotSpaceNumber],
    queryFn: async () => {
      return fetchMenuActions({owner: copilotSpaceOwner, id: copilotSpaceNumber})
    },
    enabled: copilotSpaceOwner !== '' && copilotSpaceNumber !== -1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for fetching the count of spaces for each category.
 * @param enabled - Whether to enable the fetching
 * @returns Query result with the spaces counts
 */
export function useFetchSpacesCount(enabled: boolean) {
  return useQuery({
    queryKey: COPILOT_SPACES_COUNT_QUERY_KEY,
    queryFn: fetchSpacesCount,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for fetching all categorized spaces with pagination support.
 * Each category is fetched independently using cursor-based pagination.
 *
 * @param enabled - Whether to enable the fetching of categorized spaces
 * @param options - Additional options
 * @param options.perPage - Number of items per page for each category (default: 30, max: 100)
 * @param options.individualAccessEnabled - Whether individual access is enabled (controls user_owned and shared_with_user fetching)
 * @returns Object containing categorizedSpaces data, loading states, pagination states, and fetch functions
 */
export function useFetchPaginatedCategorizedSpaces(
  enabled: boolean,
  options: {perPage?: number; individualAccessEnabled?: boolean} = {},
) {
  const {perPage = DEFAULT_PER_PAGE, individualAccessEnabled = true} = options

  const starredQuery = useFetchPaginatedCopilotSpacesByCategory('starred', enabled, {perPage})
  const userOwnedQuery = useFetchPaginatedCopilotSpacesByCategory('user_owned', enabled && individualAccessEnabled, {
    perPage,
  })
  const orgOwnedQuery = useFetchPaginatedCopilotSpacesByCategory('org_owned', enabled, {perPage})
  const sharedWithUserQuery = useFetchPaginatedCopilotSpacesByCategory(
    'shared_with_user',
    enabled && individualAccessEnabled,
    {perPage},
  )

  // Flatten spaces from each category
  const starredSpaces = useFlattenedPaginatedSpaces(starredQuery.data)
  const userOwnedSpaces = useFlattenedPaginatedSpaces(userOwnedQuery.data)
  const orgOwnedSpaces = useFlattenedPaginatedSpaces(orgOwnedQuery.data)
  const sharedWithUserSpaces = useFlattenedPaginatedSpaces(sharedWithUserQuery.data)

  const categorizedSpaces = useMemo(
    () => ({
      starred: starredSpaces,
      userOwned: userOwnedSpaces,
      orgOwned: orgOwnedSpaces,
      sharedWithUser: sharedWithUserSpaces,
    }),
    [starredSpaces, userOwnedSpaces, orgOwnedSpaces, sharedWithUserSpaces],
  )

  const categorizedLoading = useMemo(
    () => ({
      starred: starredQuery.isLoading,
      userOwned: userOwnedQuery.isLoading,
      orgOwned: orgOwnedQuery.isLoading,
      sharedWithUser: sharedWithUserQuery.isLoading,
    }),
    [starredQuery.isLoading, userOwnedQuery.isLoading, orgOwnedQuery.isLoading, sharedWithUserQuery.isLoading],
  )

  const categorizedFetchingMore = useMemo(
    () => ({
      starred: starredQuery.isFetchingNextPage,
      userOwned: userOwnedQuery.isFetchingNextPage,
      orgOwned: orgOwnedQuery.isFetchingNextPage,
      sharedWithUser: sharedWithUserQuery.isFetchingNextPage,
    }),
    [
      starredQuery.isFetchingNextPage,
      userOwnedQuery.isFetchingNextPage,
      orgOwnedQuery.isFetchingNextPage,
      sharedWithUserQuery.isFetchingNextPage,
    ],
  )

  const categorizedHasMore = useMemo(
    () => ({
      starred: starredQuery.hasNextPage,
      userOwned: userOwnedQuery.hasNextPage,
      orgOwned: orgOwnedQuery.hasNextPage,
      sharedWithUser: sharedWithUserQuery.hasNextPage,
    }),
    [starredQuery.hasNextPage, userOwnedQuery.hasNextPage, orgOwnedQuery.hasNextPage, sharedWithUserQuery.hasNextPage],
  )

  const fetchMoreStarred = useCallback(() => {
    if (starredQuery.hasNextPage && !starredQuery.isFetchingNextPage) {
      void starredQuery.fetchNextPage()
    }
  }, [starredQuery])

  const fetchMoreUserOwned = useCallback(() => {
    if (userOwnedQuery.hasNextPage && !userOwnedQuery.isFetchingNextPage) {
      void userOwnedQuery.fetchNextPage()
    }
  }, [userOwnedQuery])

  const fetchMoreOrgOwned = useCallback(() => {
    if (orgOwnedQuery.hasNextPage && !orgOwnedQuery.isFetchingNextPage) {
      void orgOwnedQuery.fetchNextPage()
    }
  }, [orgOwnedQuery])

  const fetchMoreSharedWithUser = useCallback(() => {
    if (sharedWithUserQuery.hasNextPage && !sharedWithUserQuery.isFetchingNextPage) {
      void sharedWithUserQuery.fetchNextPage()
    }
  }, [sharedWithUserQuery])

  const categorizedFetchMore = useMemo(
    () => ({
      starred: fetchMoreStarred,
      userOwned: fetchMoreUserOwned,
      orgOwned: fetchMoreOrgOwned,
      sharedWithUser: fetchMoreSharedWithUser,
    }),
    [fetchMoreStarred, fetchMoreUserOwned, fetchMoreOrgOwned, fetchMoreSharedWithUser],
  )

  return {
    categorizedSpaces,
    categorizedLoading,
    categorizedFetchingMore,
    categorizedHasMore,
    categorizedFetchMore,
  }
}

/**
 * Hook for fetching all filtered categorized spaces with their loading states.
 * This hook fetches spaces by category and search term and returns both the filtered + categorised data
 * and the loading state for each category.
 * @param enabled - Whether to enable the fetching of categorized spaces
 * @param query - The search query string
 * @param options - Additional options
 * @param options.individualAccessEnabled - Whether individual access is enabled (controls user_owned and shared_with_user fetching)
 * @returns Object containing categorizedSpaces and categorizedLoading
 */
export function useFetchFilteredCategorizedSpaces(
  enabled: boolean,
  query: string,
  options: {individualAccessEnabled?: boolean} = {},
) {
  const {individualAccessEnabled = true} = options

  const {data: starredCopilotSpaces, isLoading: isStarredCopilotSpacesLoading} =
    useFetchFilteredCopilotSpacesByCategory('starred', query, enabled)
  const {data: userOwnedCopilotSpaces, isLoading: isUserOwnedCopilotSpacesLoading} =
    useFetchFilteredCopilotSpacesByCategory('user_owned', query, enabled && individualAccessEnabled)
  const {data: orgOwnedCopilotSpaces, isLoading: isOrgOwnedCopilotSpacesLoading} =
    useFetchFilteredCopilotSpacesByCategory('org_owned', query, enabled)
  const {data: sharedWithUserCopilotSpaces, isLoading: isSharedWithUserCopilotSpacesLoading} =
    useFetchFilteredCopilotSpacesByCategory('shared_with_user', query, enabled && individualAccessEnabled)
  const filteredCategorizedSpaces = useMemo(
    () => ({
      starred: starredCopilotSpaces,
      userOwned: userOwnedCopilotSpaces,
      orgOwned: orgOwnedCopilotSpaces,
      sharedWithUser: sharedWithUserCopilotSpaces,
    }),
    [starredCopilotSpaces, userOwnedCopilotSpaces, orgOwnedCopilotSpaces, sharedWithUserCopilotSpaces],
  )

  const filteredCategorizedLoading = useMemo(
    () => ({
      starred: isStarredCopilotSpacesLoading,
      userOwned: isUserOwnedCopilotSpacesLoading,
      orgOwned: isOrgOwnedCopilotSpacesLoading,
      sharedWithUser: isSharedWithUserCopilotSpacesLoading,
    }),
    [
      isStarredCopilotSpacesLoading,
      isUserOwnedCopilotSpacesLoading,
      isOrgOwnedCopilotSpacesLoading,
      isSharedWithUserCopilotSpacesLoading,
    ],
  )

  return {filteredCategorizedSpaces, filteredCategorizedLoading}
}

/**
 * Custom copilots are enabled for:
 * 1. users with the :copilot_custom_copilots feature flag, which some will be individually opted into
 * 2. The following types of users with the :copilot_custom_copilots_feature_preview feature flag:
 *    a. users with Copilot Individual (CI) licenses, for whom preview features are always enabled
 *    b. CB and CE users whose orgs have opted into preview features
 *    c. Copilot Free users, signified by `has_limited_access` on their plan
 * This values is serialized down from the copilot_chat_helper and put into the chat state.
 */
export function useCustomCopilotsEnabled() {
  return useChatStateValue('customCopilotsEnabled')
}

/**
 * Hook that returns the current number of Copilot spaces.
 *
 * @returns The total count of existing Copilot spaces
 */
export function useNumberOfSpaces(): number {
  const copilotSpaces = useChatStateValue('customCopilots')
  const copilotSpacesCount = copilotSpaces?.length ?? 0
  return copilotSpacesCount
}

/* Generates a default name based on the current number of existing spaces.
 * The default name follows the pattern "New Space {count + 1}" where count is the number of existing spaces.
 *
 * @returns the default name as a string
 */
export function useAutoSpaceName(): string {
  const copilotSpacesCount = useNumberOfSpaces()
  const defaultName = useMemo(() => getAutoSpaceName(copilotSpacesCount), [copilotSpacesCount])
  return defaultName
}

export function getAutoSpaceName(copilotSpacesCount: number): string {
  return `New Space ${copilotSpacesCount + 1}`
}

// Hoisted to module scope so their identities are stable across renders. In
// particular, `useSyncExternalStore` re-subscribes whenever the `subscribe`
// reference changes, so an inline function would tear down and re-establish the
// (no-op) subscription on every commit. The origin never changes during a
// session, so `subscribe` is a no-op and `getSnapshot` just reads it once.
const subscribeToOrigin = () => () => {}
const getOriginSnapshot = () => ssrSafeWindow?.location.origin
const getOriginServerSnapshot = () => undefined

/** Hook to generate a report URL to Github support for a Copilot Space.
 * @param selectedCopilotSpaceId - The ID of the selected Copilot Space, or null if none is selected.
 * @param basePath - Overrides the default /copilot/spaces base for the reported content URL.
 * @returns The constructed report URL or undefined if not applicable.
 */
export function useReportUrl(selectedCopilotSpaceId: CopilotSpaceId | null, basePath?: string): string | undefined {
  const host = useSyncExternalStore(subscribeToOrigin, getOriginSnapshot, getOriginServerSnapshot)

  const reportUrl = useMemo(() => {
    if (!selectedCopilotSpaceId) return undefined
    const {owner} = selectedCopilotSpaceId
    if (!host) return undefined

    const spaceUrl = getCopilotSpacePath(selectedCopilotSpaceId, basePath)
    const fullUrl = `${host}${spaceUrl}`
    const query = `content_url=${encodeURIComponent(fullUrl)}&report=${owner}+(user)`
    const reportTargetUrl = `${host}/contact/report-content?${query}`

    return reportTargetUrl
  }, [selectedCopilotSpaceId, host, basePath])

  return reportUrl
}
